/**
 * Переменные стиль-пака по решениям владельца №4–5 (2026-10-08, повтор
 * эталона Slimkit; spec: shared/screen-container.md и shared/typography.md,
 * разделы «Что добавить у нас»). Вынесено из stylePackToStyle.ts отдельным
 * модулем, чтобы новые оси не раздували общий сборщик и проверялись отдельно.
 *
 * Инвариант «старые воронки не меняются»:
 *  - появление, поля на телефоне, интерлиньяж и трекинг — ключ появляется
 *    ТОЛЬКО когда пак задал поле; у 12 прежних паков их нет вовсе;
 *  - десктопные ДВОЙНИКИ (`--*-desktop`) отдаются всегда, но у пака без
 *    `type.desktop` они равны мобильным значениям. Нужны они потому, что
 *    медиа-запрос (public-paywall.css) переобъявляет `--h1-size` и
 *    `--heading-align` на колонке, и без двойника пак потерял бы свои числа
 *    на десктопе. Значение двойника = мобильное → вид прежний.
 *
 * Цветов модуль не эмитит (guard «пак не задаёт цвета», stylePackToStyle.test).
 */
import type { StylePack } from './stylePack.types';
import { h1SizeCss } from './styleKitDoor';

// WEB-3447, заход 2: формула кегля живёт у двери куска (первый экран) —
// двойник `--h1-size-desktop` нужен каждому паку и без куска.
export { h1SizeCss };

/** Имя keyframes «только прозрачность» в public-paywall.css. */
export const SCREEN_FADE_KEYFRAMES = 'funnel-screen-fade';

export function stylePackExtraVars(pack: StylePack): Record<string, string> {
  const { density, type, states } = pack;
  const vars: Record<string, string> = {};

  // ── появление экрана (решение №4) ───────────────────────────────────────
  // 'fade' подменяет имя анимации класса .funnel-screen-in; 'fade-up' и
  // отсутствие поля — прежний funnel-screen-in (ключа нет).
  if (states.screenEnter === 'fade') {
    vars['--screen-enter-anim'] = SCREEN_FADE_KEYFRAMES;
  }

  // ── поля на телефоне (screen-container.md п.3) ──────────────────────────
  // Страница получает ровно число пака, колонка — ноль: сумма = полю эталона.
  if (typeof density.screenPadXMobile === 'number') {
    vars['--screen-pad-x-mobile'] = `${density.screenPadXMobile}px`;
    vars['--screen-pad-x-mobile-inner'] = '0px';
  }

  // ── интерлиньяж и трекинг (typography.md п.2) ───────────────────────────
  if (typeof type.h1LineHeight === 'number') {
    vars['--h1-line-height'] = `${type.h1LineHeight}px`;
  }
  if (typeof type.subLineHeight === 'number') {
    vars['--sub-line-height'] = `${type.subLineHeight}px`;
  }
  if (typeof type.subTracking === 'number') {
    vars['--sub-tracking'] = `${type.subTracking}px`;
  }

  // ── десктопная типографика (typography.md п.4) ──────────────────────────
  const desktop = type.desktop;
  vars['--h1-size-desktop'] =
    typeof desktop?.h1Size === 'number'
      ? `${desktop.h1Size}px`
      : h1SizeCss(type.h1Size);
  vars['--heading-align-desktop'] = desktop?.headingAlign ?? type.headingAlign;
  const desktopLineHeight = desktop?.h1LineHeight ?? type.h1LineHeight;
  if (typeof desktopLineHeight === 'number') {
    vars['--h1-line-height-desktop'] = `${desktopLineHeight}px`;
  }

  return vars;
}
